// src/features/task/create/TaskTypeStrip.tsx
// The control that reshapes the whole create form: choosing a task type.
//
// Types come from `ResolveTaskContext.allowed_types` — a scope-aware list, not
// a static enum. Up to eight are shown as a radiogroup of chips (one glance,
// one click); beyond that the list becomes a Select so the strip never wraps
// into a wall.

import { useRef } from 'react';
import { useI18n } from '@react/hooks';
import type { TaskTypeDefinition } from '@core/types/task';
import { Select } from '@/components/Select';
import { Icon } from './icons';
import { typeDefIconPath, typeDefId, typeDefLabelKey } from './logic';

/** Above this count a chip strip stops being scannable and becomes a dropdown. */
const CHIP_LIMIT = 8;

export interface TaskTypeStripProps {
  types: TaskTypeDefinition[];
  /** Selected definition id (entity id, else slug). */
  value: string;
  onChange: (typeId: string) => void;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  disabled?: boolean;
}

export function TaskTypeStrip({
  types,
  value,
  onChange,
  isLoading,
  error,
  onRetry,
  disabled,
}: TaskTypeStripProps) {
  const { t } = useI18n();
  const groupRef = useRef<HTMLDivElement>(null);

  if (isLoading && types.length === 0) {
    return (
      <div className="flex flex-wrap gap-2" aria-busy="true" aria-live="polite">
        <span className="sr-only">{t('task.createForm.loadingTypes')}</span>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-9 w-28 motion-safe:animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }

  if (error && types.length === 0) {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2">
        <p className="text-sm text-destructive">{error}</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="rounded-md px-2 py-1 text-sm font-medium text-destructive underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('common.retry')}
          </button>
        )}
      </div>
    );
  }

  if (types.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">{t('task.createForm.noTypesAvailable')}</p>
    );
  }

  const label = (def: TaskTypeDefinition): string => {
    const key = typeDefLabelKey(def);
    if (key) return t(key);
    return def.display_name || def.slug;
  };

  if (types.length > CHIP_LIMIT) {
    return (
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-label={t('task.field.type')}
        options={types.map((def) => ({ value: typeDefId(def), label: label(def) }))}
        placeholder={t('task.selectType')}
      />
    );
  }

  const move = (index: number, delta: number) => {
    const next = (index + delta + types.length) % types.length;
    onChange(typeDefId(types[next]));
    const buttons = groupRef.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
    buttons?.[next]?.focus();
  };

  return (
    <div
      ref={groupRef}
      role="radiogroup"
      aria-label={t('task.field.type')}
      className="flex flex-wrap gap-2"
    >
      {types.map((def, index) => {
        const id = typeDefId(def);
        const selected = id === value;
        return (
          <button
            key={id || index}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected || (!value && index === 0) ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(id)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault();
                move(index, 1);
              } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                move(index, -1);
              }
            }}
            className={[
              'inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
              'disabled:cursor-not-allowed disabled:opacity-50',
              selected
                ? 'border-primary bg-primary/10 font-medium text-primary'
                : 'border-border text-foreground hover:border-primary/40 hover:bg-muted/50',
            ].join(' ')}
          >
            {def.color ? (
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: def.color }}
              />
            ) : (
              <Icon d={typeDefIconPath(def)} className="h-4 w-4 shrink-0" />
            )}
            {label(def)}
          </button>
        );
      })}
    </div>
  );
}
